/**
 * Appearance: colour theme and interface density.
 *
 * `<html>` always carries a *resolved* `data-theme` of `light` or `dark` — never
 * `system`. That is deliberate: it means `styles/tokens.css` needs exactly one
 * dark block keyed on `[data-theme='dark']` instead of that block plus a
 * duplicated `prefers-color-scheme` copy that could drift out of sync. The
 * inline bootstrap in `index.html` stamps the same attribute before first paint
 * so a dark-mode user never sees a white flash; this provider takes over after
 * hydration and keeps following the OS while the preference is `system`.
 *
 * Both preferences persist to localStorage, which can throw (private mode,
 * storage disabled). Every access is guarded — appearance is a nicety and must
 * never be able to break the app.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { logger } from '@/lib/logger';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

const THEME_KEY = 'alfie:theme';
const DENSITY_KEY = 'alfie:density';
/** Must match `--paper-2` in tokens.css for each theme (mobile browser chrome). */
const THEME_COLOR: Record<ResolvedTheme, string> = { light: '#faf9f7', dark: '#101113' };

const log = logger.child('theme');

interface ThemeApi {
  theme: ThemePreference;
  resolved: ResolvedTheme;
  density: Density;
  setTheme: (t: ThemePreference) => void;
  setDensity: (d: Density) => void;
}

const ThemeContext = createContext<ThemeApi | null>(null);

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — the choice still applies for this session */
  }
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(() => readStored(THEME_KEY, ['light', 'dark', 'system'] as const, 'system'));
  const [density, setDensityState] = useState<Density>(() => readStored(DENSITY_KEY, ['comfortable', 'compact'] as const, 'comfortable'));
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(theme));

  // Apply to the document. `data-theme` is the resolved value; `data-density`
  // is only set when it differs from the default so the attribute stays a
  // meaningful signal in the DOM.
  useEffect(() => {
    const next = resolveTheme(theme);
    setResolved(next);
    const root = document.documentElement;
    root.setAttribute('data-theme', next);
    if (density === 'comfortable') root.removeAttribute('data-density');
    else root.setAttribute('data-density', density);
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[next]);
  }, [theme, density]);

  // Follow the OS while the preference is `system`.
  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolved(next);
      document.documentElement.setAttribute('data-theme', next);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[next]);
      log.info('System appearance changed', { resolved: next });
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((t: ThemePreference) => {
    setThemeState(t);
    write(THEME_KEY, t);
    log.info('Theme preference set', { theme: t });
  }, []);

  const setDensity = useCallback((d: Density) => {
    setDensityState(d);
    write(DENSITY_KEY, d);
    log.info('Density set', { density: d });
  }, []);

  const value = useMemo(
    () => ({ theme, resolved, density, setTheme, setDensity }),
    [theme, resolved, density, setTheme, setDensity],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Appearance state. Safe outside a provider (returns the defaults) so a
 * component under test does not have to be wrapped just to render.
 */
export function useTheme(): ThemeApi {
  return (
    useContext(ThemeContext) ?? {
      theme: 'system',
      resolved: 'light',
      density: 'comfortable',
      setTheme: () => {},
      setDensity: () => {},
    }
  );
}
